import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Drawer,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Toolbar,
  Typography,
  Box,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import AppIcon from '../common/AppIcon';
import OrnamentalDivider from '../common/OrnamentalDivider';
import { useAuth } from '../../hooks/useAuth';

const DRAWER_WIDTH = 240;

const navItems = [
  { label: 'Dashboard', path: '/dashboard', icon: 'dashboard' as const },
  { label: 'Topics', path: '/topics', icon: 'topic' as const },
  { label: 'Tasks', path: '/tasks', icon: 'task-alt' as const },
  { label: 'Budget', path: '/budget', icon: 'wallet' as const },
  { label: 'Guests', path: '/guests', icon: 'people' as const },
  { label: 'Documents', path: '/documents', icon: 'folder' as const },
];

interface SidebarProps {
  open: boolean;
  onClose: () => void;
  onOpenSettings: () => void;
}

const Sidebar: React.FC<SidebarProps> = ({ open, onClose, onOpenSettings }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { user, logout } = useAuth();

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const drawerContent = (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <Toolbar>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 1 }}>
          <img src="/logo192.png" alt="Wedding CC" style={{ width: 32, height: 32, borderRadius: 6 }} />
          <Typography variant="h6" sx={{ color: 'primary.main', fontWeight: 700 }}>
            Wedding CC
          </Typography>
        </Box>
      </Toolbar>
      <List sx={{ px: 1, flexGrow: 1 }}>
        {navItems.map((item) => {
          const isSelected = location.pathname === item.path;
          return (
            <ListItemButton
              key={item.path}
              selected={isSelected}
              onClick={() => {
                navigate(item.path);
                if (isMobile) onClose();
              }}
              sx={{
                borderRadius: 2,
                mb: 0.5,
                position: 'relative',
                transition: 'all 0.2s ease',
                '&::before': {
                  content: '""',
                  position: 'absolute',
                  left: 0,
                  top: '20%',
                  height: '60%',
                  width: isSelected ? 3 : 0,
                  borderRadius: '0 3px 3px 0',
                  background: 'linear-gradient(180deg, #B07A8F 0%, #C9A96E 100%)',
                  transition: 'width 0.2s ease',
                },
                '&.Mui-selected': {
                  backgroundColor: 'rgba(176,122,143,0.1)',
                  color: 'primary.dark',
                  '& .MuiListItemIcon-root': { color: 'primary.dark' },
                },
                '&:hover': {
                  backgroundColor: 'rgba(176,122,143,0.06)',
                  transform: 'translateX(2px)',
                },
              }}
            >
              <ListItemIcon sx={{ minWidth: 40, color: isSelected ? 'primary.dark' : 'grey.600', transition: 'color 0.2s ease' }}>
                <AppIcon name={item.icon} />
              </ListItemIcon>
              <ListItemText primary={item.label} />
            </ListItemButton>
          );
        })}
      </List>
      <OrnamentalDivider sx={{ mx: 2, my: 1 }} />
      <Box sx={{ px: 1, py: 1 }}>
        {user && (
          <Typography variant="caption" sx={{ px: 2, color: 'text.secondary', display: 'block', mb: 0.5 }}>
            {user.full_name}
          </Typography>
        )}
        <ListItemButton
          onClick={() => {
            onOpenSettings();
            if (isMobile) onClose();
          }}
          sx={{
            borderRadius: 2,
            mb: 0.5,
            transition: 'all 0.2s ease',
            '&:hover': { backgroundColor: 'rgba(176,122,143,0.06)', transform: 'translateX(2px)' },
          }}
        >
          <ListItemIcon sx={{ minWidth: 40, color: 'grey.600' }}>
            <AppIcon name="settings" />
          </ListItemIcon>
          <ListItemText primary="Settings" />
        </ListItemButton>
        <ListItemButton
          onClick={handleLogout}
          sx={{
            borderRadius: 2,
            transition: 'all 0.2s ease',
            '&:hover': { backgroundColor: 'rgba(176,122,143,0.06)', transform: 'translateX(2px)' },
          }}
        >
          <ListItemIcon sx={{ minWidth: 40, color: 'grey.600' }}>
            <AppIcon name="logout" />
          </ListItemIcon>
          <ListItemText primary="Logout" />
        </ListItemButton>
      </Box>
    </Box>
  );

  if (isMobile) {
    return (
      <Drawer
        variant="temporary"
        open={open}
        onClose={onClose}
        sx={{ '& .MuiDrawer-paper': { width: DRAWER_WIDTH } }}
      >
        {drawerContent}
      </Drawer>
    );
  }

  return (
    <Drawer
      variant="permanent"
      sx={{
        width: DRAWER_WIDTH,
        flexShrink: 0,
        '& .MuiDrawer-paper': {
          width: DRAWER_WIDTH,
          boxSizing: 'border-box',
          borderRight: 'none',
          background: 'linear-gradient(180deg, #FFFFFF 0%, #FBF7F4 100%)',
          boxShadow: '1px 0 8px rgba(176,122,143,0.06)',
        },
      }}
    >
      {drawerContent}
    </Drawer>
  );
};

export { DRAWER_WIDTH };
export default Sidebar;
